<template>
  <!-- Layout para rutas fullscreen (sin header ni footer) -->
  <div v-if="isFullscreenRoute" class="fullscreen-view">
    <router-view />
  </div>

  <!-- Layout normal con header y footer -->
  <div v-else class="normal-view">
    <!-- Header -->
    <header class="app-header">
      <div class="header-container">
        <!-- Back button - solo si no estamos en home (visible solo en desktop) -->
        <div class="header-left desktop-only">
          <router-link v-if="!isHomeRoute" to="/" class="back-button">
            {{ displayBackToHomeText }}
          </router-link>
        </div>

        <!-- Título central -->
        <div class="header-center mt-2">
          <div class="header-text">
            <h1 class="page-title">{{ displayTitle }}</h1>
            <h2 class="page-subtitle" v-html="displaySubtitle || '&nbsp;'"></h2>
            <p class="page-subsubtitle" v-html="displaySubSubtitle || '&nbsp;'"></p>
          </div>
        </div>

        <!-- Language Selector a la derecha (visible en pantallas grandes) -->
        <div class="header-right desktop-only">
          <LanguageSelector />
        </div>

        <!-- Botón de menú hamburguesa (visible en pantallas pequeñas) -->
        <div class="header-right mobile-only">
          <button class="menu-toggle" @click="toggleMenu" :aria-label="displayMenuTitle">
            <div class="hamburger-bars">
              <span></span>
              <span></span>
              <span></span>
            </div>
          </button>
        </div>
      </div>

      <!-- Pattern line -->
      <div v-if="pattern" class="header-pattern-line" :style="{ backgroundImage: `url(${pattern})` }">
        <div v-if="country" class="header-country">
          {{ country.toUpperCase() }}
        </div>
      </div> <!-- Menú móvil desplegable -->
      <div class="mobile-menu" :class="{ 'is-open': menuOpen }">
        <div class="mobile-menu-header">
          <h3>{{ displayMenuTitle }}</h3>
          <button class="close-menu" @click="closeMenu" :aria-label="displayCloseMenuLabel">
            <span>×</span>
          </button>
        </div>
        <div class="mobile-menu-content">
          <!-- El botón de volver aparece solo si no estamos en home -->
          <router-link v-if="!isHomeRoute" to="/" class="mobile-menu-item" @click="closeMenu">
            <span class="menu-icon">↩️</span>
            <span>{{ displayBackToHomeText }}</span>
          </router-link>

          <div class="mobile-language-selector">
            <LanguageSelector />
          </div>
        </div>
      </div>
    </header>

    <!-- Main Content -->
    <main class="app-main">
      <router-view />
    </main>

    <!-- Footer -->
    <footer class="app-footer">
      <img src="/images/logo-jcyl.jpg" :alt="displayFooterLogoAlt" class="footer-logo" @error="handleImageError">
      <div class="footer-content">
        <p>{{ displayFooterCopyright }}</p>
        <div class="footer-links">
          <a href="#" @click.prevent="showCookiePolicyModal">{{ displayCookiePolicyLink }}</a>
        </div>
      </div>
    </footer>

    <!-- Componente de consentimiento de cookies -->
    <CookieConsent ref="cookieConsent" @consent-updated="handleCookieConsent" />

    <!-- Toast Container -->
    <ToastContainer :toasts="toastState.toasts" @remove-toast="removeToast" />
  </div>
</template>

<script>
import { ref, toRef } from 'vue'
import LanguageSelector from './components/LanguageSelector.vue'
import CookieConsent from './components/CookieConsent.vue'
import ToastContainer from './components/ToastContainer.vue'
import { useHeader } from './composables/useHeader.js'
import { usePartida, useGame, useScene } from './composables/usePartidaContext.js'
import { toastState } from './plugins/toast.js'
import { useConfig } from './composables/useConfig.js'
import { useLanguage } from './composables/useLanguage.js'
import iconService from '&/services/iconService.js'

export default {
  name: 'App',
  components: {
    LanguageSelector,
    CookieConsent,
    ToastContainer
  },
  setup() {
    const menuOpen = ref(false);
    const { getConfigText } = useConfig();
    const { language } = useLanguage();
    const { partida } = usePartida();
    const { game } = useGame();
    const { scene } = useScene();
    const header = useHeader();
    const domObserver = ref(null);

    return {
      menuOpen,
      toastState,
      getConfigText,
      language,
      domObserver,
      partida,
      game,
      scene,

      ...header
    }
  },
  computed: {
    headerDependencies() {
      return {
        partida: this.partida,
        game: this.game,
        language: this.language,
        scene: this.scene
      }
    },
    // Detectar si la ruta actual es fullscreen
    isFullscreenRoute() {
      return this.$route.meta?.fullscreen === true
    },

    // Detectar si estamos en la página de inicio
    isHomeRoute() {
      return this.$route.path === '/' || this.$route.name === 'Home'
    },

    // Textos configurables con fallbacks
    displayTitle() {
      if (this.title) {
        return this.title
      }
      return this.getConfigText('general.title', 'Las Dimensiones del Agua')
    },
    displaySubtitle() {
      if (this.subtitle) {
        return this.subtitle
      }
    },
    displaySubSubtitle() {
      if (this.subSubtitle) {
        return this.subSubtitle
      }
    },
    displayBackToHomeText() {
      return this.getConfigText('general.backToHomeText', 'Volver al inicio')
    },
    displayFooterCopyright() {
      return this.getConfigText('general.footerCopyright', '© 2025 Asociación de Cooperación por la Paz - Todos los derechos reservados')
    },
    displayMenuTitle() {
      return this.getConfigText('general.menuTitle', 'Menú')
    },
    displayCloseMenuLabel() {
      return this.getConfigText('general.closeMenuLabel', 'Cerrar menú')
    },
    displayFooterLogoAlt() {
      return this.getConfigText('general.footerLogoAlt', 'Logo Junta de Castilla y León')
    },
    displayCookiePolicyLink() {
      return this.getConfigText('general.cookiePolicyLinkText', 'Política de cookies')
    }
  },
  watch: {
    // Actualizar título cuando cambie la ruta
    '$route'() {
      this.clearHeader()
      this.updateDocumentTitle()
      this.closeMenu()

      if (!this.partida || !this.game || !this.game.background_color) {
        this.resetGameColor()
      }
    },
    // Vigilar cambios en el color del header
    gameColor(newColor) {
      this.applyGameColor(newColor)
    },
    // Vigilar cambios en el título del header
    title(newTitle) {
      this.updateDocumentTitle()
    },
    // Vigilar cambios en el idioma
    language(newLanguage) {
      console.log('🌐 [App] Idioma cambiado:', newLanguage);
      this.handleLanguageChange();
    },
    headerDependencies() {
      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.initializeQlEditorIframes();
          });
        });
      })
    }
  },
  async mounted() {
    await this.initializeApp()

    // Añadir event listener para cerrar el menú al hacer clic fuera
    document.addEventListener('click', this.handleOutsideClick)
    window.addEventListener('resize', this.initializeQlEditorIframes);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick)
    window.removeEventListener('resize', this.initializeQlEditorIframes);
  },
  methods: {
    initializeQlEditorIframes() {
      // Inicializar iframes de QL Editor si existen
      const iframes = document.querySelectorAll('.ql-editor iframe[data-filename]');
      iframes.forEach(iframe => {
        // permitimos fullscreen
        iframe.setAttribute('frameborder', '0');
        // aplicamos estilo de aspect ratio segun data-width y data-height
        const width = iframe.getAttribute('data-width');
        const height = iframe.getAttribute('data-height');
        if (width && height) {
          iframe.style.aspectRatio = `${width} / ${height}`;
        }

        // segun el ancho actual del iframe, lo ajusttamos segun el data-width y data-height. 
        const currentWidth = iframe.clientWidth;
        iframe.style.width = '100%';
        if (width && height && currentWidth < width) {
          const aspectRatio = height / width;
          iframe.style.height = `${currentWidth * aspectRatio}px`;
        } else if (width && height) {
          iframe.style.height = `${height}px`;
        }
        console.log('🎮 [App] Partida cambiada, re-inicializando iframes de QL Editor');
      });
    },
    async initializeApp() {
      console.log('🚀 [App] Inicializando aplicación...')

      // Inicializar iconService con el idioma actual
      await iconService.init(this.language);

      // Actualizar título del documento
      this.updateDocumentTitle()

      // Solo resetear el color si no hay un color específico del juego
      if (!this.partida || !this.game || !this.game.background_color) {
        this.resetGameColor()
      }

      console.log('✅ [App] Aplicación inicializada')
    },

    updateDocumentTitle() {
      const configTitle = this.getConfigText('general.title', 'Las Dimensiones del Agua')
      const dynamicTitle = this.title ? ' - ' + this.title : '';
      document.title = configTitle + dynamicTitle
    },

    async handleLanguageChange() {
      this.updateDocumentTitle()

      // Actualizar favicon según el nuevo idioma
      await iconService.updateFavicon(this.language);

      this.$nextTick(() => {
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            this.initializeQlEditorIframes();
          });
        });
      })

      // Cerrar el menú móvil si está abierto cuando se cambia el idioma
      if (this.menuOpen) {
        this.closeMenu()
      }
    },

    // Método alternativo para esperar a que el DOM termine de cambiar
    waitForDOMChangesAlternative() {
      return new Promise((resolve) => {
        let timeoutId;

        // Crear un MutationObserver para detectar cambios en el DOM
        const observer = new MutationObserver((mutations) => {
          // Reiniciar el timeout cada vez que hay un cambio
          clearTimeout(timeoutId);

          // Esperar 50ms después del último cambio para considerar que terminó
          timeoutId = setTimeout(() => {
            observer.disconnect();
            resolve();
          }, 50);
        });

        // Observar cambios en todo el documento
        observer.observe(document.body, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeOldValue: true,
          characterData: true,
          characterDataOldValue: true
        });

        // Fallback: resolver después de 500ms máximo
        setTimeout(() => {
          observer.disconnect();
          resolve();
        }, 500);
      });
    },

    // Aplicar el color del juego a los elementos UI
    applyGameColor(color) {
      // Si no hay color, usar el color por defecto
      const backgroundColor = color || this.gameColor;
      // Calculamos un color más oscuro para efectos hover
      const darkerColor = this.getDarkerColor(backgroundColor, 20);
      const darkestColor = this.getDarkerColor(backgroundColor, 60);

      document.documentElement.style.setProperty('--game-color', backgroundColor);
      document.documentElement.style.setProperty('--game-color-dark', darkerColor);
      document.documentElement.style.setProperty('--game-gradient',
        `linear-gradient(135deg, ${backgroundColor} 0%, ${darkestColor} 100%)`);

      console.log('🎨 [App] Color de juego aplicado:', backgroundColor);
    },

    // Función para obtener un color más oscuro a partir de un color hex
    getDarkerColor(hex, percent) {
      // Convertir hex a RGB
      let r = parseInt(hex.substring(1, 3), 16);
      let g = parseInt(hex.substring(3, 5), 16);
      let b = parseInt(hex.substring(5, 7), 16);

      // Reducir cada componente por el porcentaje especificado
      r = Math.floor(r * (100 - percent) / 100);
      g = Math.floor(g * (100 - percent) / 100);
      b = Math.floor(b * (100 - percent) / 100);

      // Convertir de nuevo a hex
      return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
    },

    handleCookieConsent(status) {
      console.log(`🍪 [App] Consentimiento de cookies: ${status}`)
      // Aquí puedes implementar la lógica adicional basada en el consentimiento
    },
    showCookiePolicyModal() {
      if (this.$refs.cookieConsent) {
        this.$refs.cookieConsent.openPolicyModal()
      }
    },
    handleImageError(event) {
      console.warn('⚠️ [App] Error cargando imagen:', event.target.src)
      // Ocultar imagen si no se puede cargar
      event.target.style.display = 'none'
    },

    toggleMenu() {
      this.menuOpen = !this.menuOpen;
      // Si se abre el menú, añadimos clase al body para evitar scroll
      if (this.menuOpen) {
        document.body.classList.add('menu-open');
      } else {
        document.body.classList.remove('menu-open');
      }
    },

    closeMenu() {
      this.menuOpen = false;
      document.body.classList.remove('menu-open');
    },

    handleOutsideClick(event) {
      // Solo procesar si el menú está abierto
      if (!this.menuOpen) return;

      // Obtener el elemento del menú y el botón toggle
      const mobileMenu = document.querySelector('.mobile-menu');
      const menuToggle = document.querySelector('.menu-toggle');

      // Verificar si el clic fue fuera del menú y del botón toggle
      if (mobileMenu &&
        !mobileMenu.contains(event.target) &&
        menuToggle &&
        !menuToggle.contains(event.target)) {
        this.closeMenu();
      }
    },

    // Método para resetear el color del juego al color por defecto
    resetGameColor() {
      this.gameColor = '#6440A4';
      this.applyGameColor(this.gameColor);
      console.log('🔄 [App] Color reseteado al valor por defecto:', this.gameColor);
    },

    // Método para eliminar toast
    removeToast(id) {
      this.$toast?.remove(id)
    }
  }
}
</script>

<style>
/* Layout base */
* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

:root {
  /* Colores base de la aplicación */
  --app-color: #667eea;
  --app-color-dark: #4b5fcf;
  --app-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);

  /* Colores del juego activo (dinámicos) */
  --game-color: #1a3c6e;
  --game-color-dark: #132d53;
  --game-gradient: linear-gradient(135deg, #1a3c6e 0%, #051b38 100%);

  /* Colores de interfaz */
  --text-light: #ffffff;
  --text-dark: #333333;
  --background-light: rgba(255, 255, 255, 0.95);
  --background-semi: rgba(255, 255, 255, 0.3);
  --background-transparent: rgba(255, 255, 255, 0.2);
}

body {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue',
    sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  background: var(--app-gradient);
  min-height: 100vh;
  background-attachment: fixed;
}

/* Estilos globales para imágenes del rich text editor */
.ql-editor img,
.quill-content img,
.rich-text-content img,
[v-html] img {
  max-width: 100% !important;
  height: auto !important;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Fullscreen layout */
.fullscreen-view {
  min-height: var(--app-height);
  width: 100%;
}

/* Normal layout */
.normal-view {
  min-height: var(--app-height);
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100vw;
  overflow-x: hidden;
}

/* Main content */
.app-main {
  flex: 1;
  width: 100%;
  max-width: 100vw;
  box-sizing: border-box;
}

/* Header styles */
.app-header {
  background: var(--game-gradient);
  color: var(--text-light);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  min-height: 100px;
  display: flex;
  flex-direction: column;
}

.header-pattern-line {
  width: 100%;
  height: 40px;
  background-size: cover;
  background-position: center;
  background-repeat: repeat-x;
  margin-top: auto;
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.header-country {
  color: white;
  font-weight: bold;
  font-size: var(--form-font-size);
  letter-spacing: 1px;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.7);
  background: rgba(0, 0, 0, 0.3);
  padding: var(--button-padding-y) var(--button-padding-x);
  border-radius: 15px;
  backdrop-filter: blur(5px);
}

.header-container {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  max-width: 1200px;
  margin: var(--spacing-lg) auto 0 auto;
  padding: 0 var(--container-padding);
  box-sizing: border-box;
  width: 100%;
  flex: 1;
}

.header-left,
.header-right {
  min-width: 120px;
}

.header-left {
  display: flex;
  justify-content: flex-start;
  grid-column: 1;
}

.header-center {
  text-align: center;
  grid-column: 2;
}

.header-right {
  display: flex;
  justify-content: flex-end;
  grid-column: 3;
}

/* Asegurar que el título central siempre esté centrado */
.header-text {
  white-space: nowrap;
  margin: 0 auto;
}

/* Clases para mostrar/ocultar elementos en móvil/desktop */
.mobile-only {
  display: none;
}

@media (max-width: 1024px) {
  .mobile-only {
    display: flex;
  }

  .desktop-only {
    display: none;
  }
}

/* Estilos del selector de idioma */
.header-right .language-selector {
  margin: 0;
}

.header-right .language-selector .language-label {
  color: white !important;
  font-weight: 500;
  font-size: var(--form-font-size);
  margin-right: var(--spacing-sm);
  opacity: 1 !important;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
}

.header-right .language-selector .language-dropdown {
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.3) !important;
  color: #2c3e50 !important;
  font-size: var(--button-font-size);
  padding: var(--button-padding-y) var(--button-padding-x);
  border-radius: 8px;
  min-width: 120px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
}

.header-right .language-selector .language-dropdown:hover {
  background: white !important;
  border-color: rgba(255, 255, 255, 0.8) !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
}

.header-right .language-selector .language-dropdown:focus {
  border-color: #4facfe !important;
  box-shadow: 0 0 0 3px rgba(79, 172, 254, 0.3) !important;
  outline: none;
}

.back-button {
  background: rgba(255, 255, 255, 0.2);
  color: white;
  text-decoration: none;
  padding: var(--button-padding-y) var(--button-padding-x);
  border-radius: 20px;
  font-size: var(--button-font-size);
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
  display: inline-block;
}

.back-button:hover {
  background: rgba(255, 255, 255, 0.3);
  color: white;
  transform: translateX(-2px);
  text-decoration: none;
}

.page-title {
  font-size: var(--heading-font-size-h1);
  font-weight: 700;
  margin-bottom: var(--spacing-sm);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.page-subtitle {
  font-size: var(--heading-font-size-h4);
  font-weight: 700;
  opacity: 0.9;
  margin-bottom: var(--spacing-xs);
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
}

.page-subsubtitle {
  font-size: var(--card-text-font-size);
  opacity: 0.85;
  font-weight: 300;
  line-height: var(--body-line-height);
  min-height: var(--body-line-height);
  margin-top: var(--spacing-xs);
}

/* Main content */
.app-main {
  flex: 1;
  width: 100%;
  padding: var(--section-padding) var(--section-padding);
}

/* Footer styles */
.app-footer {
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%);
  color: white;
  text-align: center;
  padding: var(--button-padding-y);
  margin-top: auto;
  min-height: 90px;
  width: 100%;
  box-sizing: border-box;
}

.app-footer p {
  margin: 0;
  font-size: var(--form-font-size);
  opacity: 0.8;
}

.footer-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: 10px;
}

.footer-links {
  margin: var(--spacing-xs);
}

.footer-links a {
  color: white;
  text-decoration: none;
  font-size: var(--form-font-size);
  opacity: 0.8;
  transition: opacity 0.2s;
}

.footer-links a:hover {
  opacity: 1;
  text-decoration: underline;
}

.footer-logo {
  height: 60px;
  margin-bottom: 15px;
}

/* Menú hamburguesa */
.menu-toggle {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  cursor: pointer;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border-radius: 50%;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.1);
}

.menu-toggle:hover {
  background: rgba(255, 255, 255, 0.3);
}

.hamburger-bars {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  width: 20px;
  height: 16px;
}

.hamburger-bars span {
  display: block;
  height: 2px;
  width: 100%;
  background-color: white;
  border-radius: 2px;
  transition: all 0.3s ease;
}

.menu-open .menu-toggle {
  background: rgba(255, 255, 255, 0.3);
}

.menu-open .hamburger-bars span:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}

.menu-open .hamburger-bars span:nth-child(2) {
  opacity: 0;
}

.menu-open .hamburger-bars span:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

/* Menú móvil */
.mobile-menu {
  position: fixed;
  top: 0;
  right: -300px;
  width: 80vw;
  max-width: 200px;
  height: var(--app-height);
  background: linear-gradient(135deg, #4F6EEB 0%, #6440A4 100%);
  z-index: 1000;
  box-shadow: -5px 0 20px rgba(0, 0, 0, 0.25);
  transition: all 0.3s ease;
  overflow-y: auto;
  border-left: 1px solid rgba(255, 255, 255, 0.2);
}

.mobile-menu.is-open {
  right: 0;
}

.mobile-menu-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md) var(--spacing-sm) var(--spacing-xl) var(--spacing-2xl);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
}

.mobile-menu-header h3 {
  font-size: var(--heading-font-size-h3);
  font-weight: 600;
  color: white;
  margin: 0;
}

.close-menu {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: white;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: var(--heading-font-size-h2);
  line-height: 1;
  padding: 0;
}

.close-menu:hover {
  background: rgba(255, 255, 255, 0.3);
}

.mobile-menu-content {
  display: flex;
  flex-direction: column;
}

.mobile-menu-item {
  color: white;
  text-decoration: none;
  padding: var(--card-padding);
  font-size: var(--heading-font-size-h4);
  border-radius: 8px;
  margin-bottom: var(--spacing-sm);
  display: flex;
  align-items: center;
  gap: 0.75rem;
  transition: background-color 0.2s ease;
}

.mobile-menu-item:hover {
  background: rgba(255, 255, 255, 0.1);
}

.menu-icon {
  font-size: var(--heading-font-size-h3);
  width: 24px;
  display: inline-flex;
  justify-content: center;
}

.mobile-language-selector {
  padding: var(--spacing-xl);
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.05);
}

.mobile-language-selector h4 {
  color: white;
  font-size: var(--font-size-base);
  margin: 0 0 1rem 0;
  font-weight: 600;
  opacity: 0.9;
}

.mobile-language-selector .language-selector {
  flex-direction: column;
  align-items: flex-start;
  gap: 0.75rem;
}

.mobile-language-selector .language-label {
  color: white;
  font-size: var(--body-font-size);
  margin-bottom: var(--spacing-sm);
}

.mobile-language-selector .language-dropdown {
  width: 100%;
  padding: var(--spacing-md);
  font-size: var(--font-size-base);
  background: rgba(255, 255, 255, 0.95);
  border: none;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

/* Overlay para cerrar el menú al hacer clic fuera */
.menu-open::before {
  content: '';
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 999;
  cursor: pointer;
}

/* Evitar scroll del body cuando el menú está abierto */
body.menu-open {
  overflow: hidden;
}

.ql-editor img,
.ql-editor iframe {
  max-width: 100%;
  width: 100%;
  border: 0px;
}

/* Responsive design */
@media (max-width: 1024px) {
  .app-header {
    min-height: 70px;
  }

  .header-container {
    grid-template-columns: auto 1fr auto;
    gap: 0.5rem;
    padding: var(--button-padding-y) 0.75rem;
    margin: var(--spacing-sm) auto 0 auto;
  }

  .header-left {
    justify-content: flex-start;
    min-width: auto;
  }

  .header-center {
    text-align: center;
    grid-column: 2;
  }

  .header-right {
    min-width: auto;
    padding-right: 0;
  }

  .header-text {
    text-align: center;
    padding: 0 0.5rem;
  }

  .page-title {
    font-size: var(--heading-font-size-h2);
    margin-bottom: 0.3rem;
  }

  .page-subtitle {
    font-size: var(--body-font-size);
    margin-bottom: 0.2rem;
  }

  .page-subsubtitle {
    font-size: var(--body-font-size-sm);
    min-height: 1.2rem;
  }

  .back-button {
    padding: var(--button-padding-y) var(--button-padding-x);
    font-size: var(--body-font-size-sm);
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: var(--heading-font-size-h3);
  }

  .page-subtitle {
    font-size: var(--form-font-size);
  }

  .page-subsubtitle {
    font-size: var(--body-font-size-sm);
  }

  .header-container {
    padding: var(--button-padding-y) 0.5rem;
    gap: 0.25rem;
  }

  .menu-toggle {
    width: 40px;
    height: 40px;
    margin-right: var(--spacing-sm)
  }

  .hamburger-bars {
    width: 18px;
    height: 14px;
  }
}

/* Estilos específicos para pantallas muy pequeñas (360px) */
@media (max-width: 360px) {
  .header-container {
    padding: var(--spacing-xs) calc(var(--spacing-xs) / 2);
    gap: 0.05rem;
  }

  .page-title {
    font-size: var(--font-size-2xl);
    margin-bottom: 0.125rem;
  }

  .page-subtitle {
    font-size: var(--font-size-md);
    margin-bottom: 0.0625rem;
  }

  .page-subsubtitle {
    font-size: var(--font-size-xs);
    margin-bottom: 0.0625rem;
  }

  .menu-toggle {
    width: 32px;
    height: 32px;
    padding: var(--spacing-xs);
    margin-right: var(--spacing-lg);
  }

  .hamburger-bars {
    width: 14px;
    height: 10px;
  }

  .mobile-menu {
    width: 95vw;
    right: -95vw;
    padding: var(--spacing-md);
  }

  .mobile-menu-item {
    padding: var(--spacing-xl) var(--spacing-sm);
    font-size: var(--body-font-size-sm);
  }
}
</style>